import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import useClickOutside from 'utils/useClickOutside';
import DateRangeInputs from '../DateRangeInputs/DateRangeInputs';
import DateRangeCalendarDropDown from '../DateRangeCalendarDropDown/DateRangeCalendarDropDown';

interface DateRangeSelectorProps {
  dateFormat?: string;
  disabled?: boolean;
  endDate: Date | null;
  isDayHighlighted: (date: Date) => boolean;
  onDateChange: ({
    startDate,
    endDate,
  }: {
    startDate: Date | null;
    endDate: Date | null;
  }) => void;
  position?: 'left' | 'right';
  startDate: Date | null;
  onPrevMonthClick?: () => void;
  onNextMonthClick?: () => void;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  dateFormat,
  disabled = false,
  endDate,
  isDayHighlighted,
  onDateChange,
  position = 'left',
  startDate,
  onPrevMonthClick,
  onNextMonthClick,
}) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => {
    setIsCalendarOpen(false);
  });

  const handleCalendarOpen = () => {
    if (disabled) return;
    setIsCalendarOpen(true);
  };

  const handleDateChange = ({
    startDate,
    endDate,
  }: {
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    onDateChange({ startDate, endDate });
  };

  const handleClose = () => {
    setIsCalendarOpen(false);
  };

  return (
    <DateRangeSelectorContainer>
      <DateRangeInputs
        startDate={startDate}
        endDate={endDate}
        onStartDateClick={handleCalendarOpen}
        onEndDateClick={handleCalendarOpen}
        disabled={disabled}
        dateFormat={dateFormat}
        firstPlaceholder="arrival"
        secondPlaceholder="departure"
      />

      {isCalendarOpen && (
        <div ref={containerRef}>
          <DateRangeCalendarDropDown
            dropDownRef={containerRef}
            endDateCalendar={endDate}
            handleClose={handleClose}
            handleOnDateChange={handleDateChange}
            isDayHighlighted={isDayHighlighted}
            position={position}
            showDropDown={isCalendarOpen}
            startDateCalendar={startDate}
            onPrevMonthClick={onPrevMonthClick}
            onNextMonthClick={onNextMonthClick}
          />
        </div>
      )}
    </DateRangeSelectorContainer>
  );
};

const DateRangeSelectorContainer = styled.div`
  position: relative;
  width: 100%;
`;

export default DateRangeSelector;
